import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Mail } from "lucide-react";
import { api } from "../lib/api";

export default function MembershipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/api/membership/status/${sessionId}`);
        if (cancelled) return;
        if (data.granted) {
          setState({ status: "granted" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attempts >= 10) {
          setState({ status: "processing" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 10) {
          setState({ status: "processing" });
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="container-jcc py-24 max-w-xl" data-testid="page-membership-success">
      {state.status === "loading" && (
        <div className="text-center" data-testid="membership-success-loading">
          <Clock className="w-10 h-10 mx-auto text-purple animate-pulse" />
          <h1 className="mt-4 font-display text-3xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-ink-muted">This usually takes a few seconds.</p>
        </div>
      )}
      {state.status === "granted" && (
        <div className="text-center" data-testid="membership-success-granted">
          <CheckCircle2 className="w-12 h-12 mx-auto text-purple" />
          <h1 className="mt-4 font-display text-3xl font-bold">You're in.</h1>
          <p className="mt-2 text-ink-muted">
            Your Carer Essentials membership is active. Sign in with your email to open your workspace.
          </p>
          <Link
            to="/auth/handover/request?next=%2Fworkspace%2Fcarer-essentials"
            className="btn-purple mt-6 inline-flex"
            data-testid="membership-success-signin-link"
          >
            Sign in to go to Carer Essentials
          </Link>
        </div>
      )}
      {state.status === "processing" && (
        <div className="text-center" data-testid="membership-success-processing">
          <Mail className="w-12 h-12 mx-auto text-purple" />
          <h1 className="mt-4 font-display text-3xl font-bold">Payment received — finalising.</h1>
          <p className="mt-3 text-ink-muted">
            Thanks. Your payment has been received and is being confirmed. This usually takes under a minute — no
            need to refresh urgently.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/auth/handover/request?next=%2Fworkspace%2Fcarer-essentials" className="btn-purple" data-testid="membership-success-signin-link-processing">
              Sign in to go to Carer Essentials
            </Link>
            <Link to="/contact" className="btn-outline">Contact us</Link>
          </div>
        </div>
      )}
      {state.status === "expired" && (
        <div className="text-center" data-testid="membership-success-expired">
          <XCircle className="w-12 h-12 mx-auto text-amber-500" />
          <h1 className="mt-4 font-display text-3xl font-bold">Checkout session expired.</h1>
          <p className="mt-2 text-ink-muted">No charge was made. You can start a new checkout whenever you're ready.</p>
          <Link to="/membership/carer-profile" className="btn-purple mt-6 inline-flex">Try again</Link>
        </div>
      )}
      {state.status === "error" && (
        <div className="text-center" data-testid="membership-success-error">
          <XCircle className="w-12 h-12 mx-auto text-red-600" />
          <h1 className="mt-4 font-display text-3xl font-bold">We couldn't find that session.</h1>
          <p className="mt-2 text-ink-muted">Please contact us if you've been charged but don't see your membership active.</p>
          <Link to="/contact" className="btn-outline mt-6 inline-flex">Contact us</Link>
        </div>
      )}
    </div>
  );
}
